import { toast } from "react-hot-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { UserPlus, Check, Users, Mail, Shield, Building2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useInviteOrganizationMember } from "@/hooks/api-hooks";

const addMemberSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  firstName: z.string().min(2, "First name must be at least 2 characters").optional(),
  lastName: z.string().min(2, "Last name must be at least 2 characters").optional(),
  role: z.enum(['admin', 'manager', 'staff'], {
    required_error: "Please select a role",
  }),
  departmentId: z.string().optional(),
  managerId: z.string().optional(),
  teamIds: z.array(z.string()).default([]),
  sendWelcomeEmail: z.boolean().default(true),
});

type AddMemberFormData = z.infer<typeof addMemberSchema>;

interface Manager {
  id: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  role: string;
}

interface Team {
  id: string;
  name: string;
  description?: string;
}

interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (memberData: any) => void;
  organizationId: string;
  managers: Manager[];
  teams?: Team[];
}

export function AddMemberModal({
  isOpen,
  onClose,
  onSuccess,
  organizationId,
  managers,
  teams = []
}: AddMemberModalProps) {
  const inviteMemberMutation = useInviteOrganizationMember();

  const form = useForm<AddMemberFormData>({
    resolver: zodResolver(addMemberSchema),
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      role: "staff",
      departmentId: "",
      managerId: "",
      teamIds: [],
      sendWelcomeEmail: true,
    },
  });

  const watchedRole = form.watch("role");
  const watchedTeamIds = form.watch("teamIds");

  const handleTeamToggle = (teamId: string, checked: boolean) => {
    const currentTeamIds = form.getValues("teamIds");
    if (checked) {
      form.setValue("teamIds", [...currentTeamIds, teamId]);
    } else {
      form.setValue("teamIds", currentTeamIds.filter(id => id !== teamId));
    }
  };

  const onSubmit = async (data: AddMemberFormData) => {
    const { sendWelcomeEmail: _sendWelcomeEmail, ...payload } = data;

    inviteMemberMutation.mutate(
      { organizationId, ...payload },
      {
        onSuccess: (response) => {
          toast.success(`Invitation sent to ${data.email}!`);

          onSuccess?.(response);
          onClose();
          form.reset();
        },
        onError: (error: any) => {
          console.error("Error inviting member:", error);
          toast.error(`Failed to invite member: ${error?.message || 'Please try again.'}`);
        }
      }
    );
  };

  const handleClose = () => {
    onClose();
    form.reset();
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'manager':
        return 'bg-blue-100 text-blue-800';
      case 'staff':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-green-100 to-blue-100 rounded-lg flex items-center justify-center">
              <UserPlus className="h-4 w-4 text-green-600" />
            </div>
            Invite Team Member
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email Address *
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="john.doe@company.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="John" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Role *
                    </FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="staff">
                          <div className="flex items-center gap-2">
                            <Badge className={getRoleColor('staff')}>Staff</Badge>
                            <span className="text-xs text-muted-foreground">Regular team member</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="manager">
                          <div className="flex items-center gap-2">
                            <Badge className={getRoleColor('manager')}>Manager</Badge>
                            <span className="text-xs text-muted-foreground">Team lead with management permissions</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="admin">
                          <div className="flex items-center gap-2">
                            <Badge className={getRoleColor('admin')}>Admin</Badge>
                            <span className="text-xs text-muted-foreground">Full organization access</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {(watchedRole === 'manager' || watchedRole === 'staff') && managers.length > 0 && (
                <FormField
                  control={form.control}
                  name="managerId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Direct Manager
                      </FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a manager (optional)" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {managers.map((manager) => (
                            <SelectItem key={manager.user.id} value={manager.user.id}>
                              <div className="flex items-center gap-2">
                                <span>{manager.user.firstName} {manager.user.lastName}</span>
                                <Badge variant="secondary" className="text-xs">
                                  {manager.role}
                                </Badge>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {teams.length > 0 && (
                <div className="space-y-4">
                  <FormLabel className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Team Assignments (Optional)
                  </FormLabel>
                  <div className="grid grid-cols-1 gap-3 max-h-48 overflow-y-auto">
                    {teams.map((team) => (
                      <div key={team.id} className="flex items-center space-x-3 p-3 border border-border rounded-lg">
                        <Checkbox
                          checked={watchedTeamIds.includes(team.id)}
                          onCheckedChange={(checked) => handleTeamToggle(team.id, checked as boolean)}
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{team.name}</div>
                          {team.description && (
                            <div className="text-xs text-muted-foreground">{team.description}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {watchedTeamIds.length > 0 && (
                    <div className="text-sm text-muted-foreground">
                      {watchedTeamIds.length} team{watchedTeamIds.length !== 1 ? 's' : ''} selected
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="pt-6 border-t border-border space-y-4">
              <h3 className="text-sm font-semibold text-foreground">Notification Settings</h3>
              <div className="space-y-3">
                <FormField
                  control={form.control}
                  name="sendWelcomeEmail"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-2xl border border-border bg-muted/30 px-4 py-3">
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">Send Welcome Email</span>
                        <span className="text-xs text-muted-foreground">Send an invitation email to the new team member</span>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={(checked) => field.onChange(Boolean(checked))}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={inviteMemberMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={inviteMemberMutation.isPending}
                className="gap-2"
              >
                {inviteMemberMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-border border-t-foreground"></div>
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Send Invitation
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
